import { useCallback, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { classNames } from '@framework/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePopupRef } from '@framework/hooks';
import { faAngleRight, faArrowUp, faArrowDown, faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { StickyZoneTitlePosition } from '../../domain';

type Props = {
  onChangeTitlePosition(titlePosition: StickyZoneTitlePosition): void;
  onOpenSubMenu(menuType: string): void;
};

export const ZoneTitlePositionMenuButton = forwardRef<
  {
    closePopup: () => void;
  },
  Props
>(({ onChangeTitlePosition, onOpenSubMenu }, ref) => {
  const { t } = useTranslation();
  const { isOpen, popupRef, handleToggle, handleClose } = usePopupRef<HTMLDivElement>(false);

  useImperativeHandle(ref, () => ({
    closePopup: () => handleClose(),
  }));

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      onOpenSubMenu('zoneTitlePosition');
      handleToggle();
    },
    [handleToggle, onOpenSubMenu]
  );

  return (
    <div className="relative hover:bg-gray-200">
      <button
        // data-right-click-menu は親 item をクリックしても閉じないようにするためのマーカー
        data-right-click-menu
        className={classNames('flex w-full cursor-pointer px-8 py-4')}
        onClick={handleClick}
      >
        <div className="inline-block pl-4 pr-6">{t('canvas.menu.zone.titlePosition')}</div>
        <FontAwesomeIcon icon={faAngleRight} className="ml-auto" />
      </button>
      <div
        className={classNames(
          'absolute left-full top-0 z-50 w-max rounded-lg bg-white py-4 shadow-2xl',
          isOpen ? 'block' : 'hidden'
        )}
        ref={popupRef}
      >
        <button
          className="flex w-full py-6 pl-8 pr-12 hover:bg-gray-300"
          onClick={() => onChangeTitlePosition(StickyZoneTitlePosition.Top)}
        >
          <FontAwesomeIcon icon={faArrowUp} />
          <span className="flex-none pl-4">{t('canvas.zone.titlePosition.top')}</span>
        </button>
        <button
          className="flex w-full py-6 pl-8 pr-12 hover:bg-gray-300"
          onClick={() => onChangeTitlePosition(StickyZoneTitlePosition.Bottom)}
        >
          <FontAwesomeIcon icon={faArrowDown} />
          <span className="flex-none pl-4">{t('canvas.zone.titlePosition.bottom')}</span>
        </button>
        <button
          className="flex w-full py-6 pl-8 pr-12 hover:bg-gray-300"
          onClick={() => onChangeTitlePosition(StickyZoneTitlePosition.Left)}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          <span className="flex-none pl-4">{t('canvas.zone.titlePosition.left')}</span>
        </button>
        <button
          className="flex w-full py-6 pl-8 pr-12 hover:bg-gray-300"
          onClick={() => onChangeTitlePosition(StickyZoneTitlePosition.Right)}
        >
          <FontAwesomeIcon icon={faArrowRight} />
          <span className="flex-none pl-4">{t('canvas.zone.titlePosition.right')}</span>
        </button>
      </div>
    </div>
  );
});

ZoneTitlePositionMenuButton.displayName = 'ZoneTitlePositionMenuButton';
